// Logika pulpitu wydzielona z widoku. Testy jednostkowe (vitest) nie montują
// komponentów — vitest.config.mjs celowo nie ładuje pluginu Vue — więc wszystko,
// co ma być pokryte testami (formatowanie, delty, przeliczenie payloadu na
// dataset wykresu, pobranie statystyk), mieszka tutaj, a Dashboard.vue dokłada
// już tylko szablon i tłumaczenia.
//
// Kontrakt GET /admin/stats/orders:
//   { currency, periods: { today, yesterday, yesterdaySameTime, thisWeek,
//     thisMonth, prevMonthToDate }, last30days: { biggestOrder, avgOrderGross,
//     paymentSuccessRate, requestOrders, paidOrders, daily[] } }
// Pola biggestOrder, avgOrderGross i paymentSuccessRate bywają nullem (pusta
// baza, brak płatności) — każda wartość przechodzi więc przez formatery, które
// zamiast NaN/undefined zwracają EMPTY_VALUE.
import axios from 'axios'
import moment from 'moment'
import { Const } from '../config'

// Półpauza zamiast pustego miejsca: pusta karta wygląda jak błąd renderowania.
export const EMPTY_VALUE = '—'

// Locale awaryjne, gdy VUE_APP_LANG nie jest poprawnym tagiem BCP 47.
export const FALLBACK_LOCALE = 'en'

export const CHART_LABEL_FORMAT = 'DD.MM'
export const CHART_TOOLTIP_DATE_FORMAT = 'DD.MM.YYYY'
export const DATE_TIME_FORMAT = 'DD.MM.YYYY, HH:mm'

// Osie wykresu: słupki (liczba zamówień) po lewej, linia (obrót) po prawej.
export const ORDERS_AXIS_ID = 'orders'
export const GROSS_AXIS_ID = 'gross'

// Kolory z palety szablonu (_variables.scss: info / success).
export const ORDERS_COLOR = 'rgba(0, 150, 214, 0.65)'
export const GROSS_COLOR = '#1bcfb4'

const CURRENCY_CODE = /^[A-Za-z]{3}$/

const DELTA_ICONS = {
    up: 'mdi mdi-arrow-up',
    down: 'mdi mdi-arrow-down',
    flat: ''
}

const DELTA_TEXT_CLASSES = {
    up: 'text-success',
    down: 'text-danger',
    flat: 'text-muted'
}

function isNumber(value) {
    return typeof value === 'number' && isFinite(value)
}

// Intl rzuca RangeError na niepoprawny tag języka, a VUE_APP_LANG jest
// wpisywany ręcznie w .env.<locale> — nie chcemy z tego powodu wywrócić pulpitu.
function intlFormat(value, locale, options) {
    try {
        return new Intl.NumberFormat(locale, options).format(value)
    } catch (error) {
        return new Intl.NumberFormat(FALLBACK_LOCALE, options).format(value)
    }
}

// Waluta przychodzi z payloadu, więc może być pusta albo nieznana Intl —
// wtedy pokazujemy samą kwotę zamiast tracić całą wartość na wyjątku.
export function formatMoney(value, currency, locale) {
    if (!isNumber(value)) {
        return EMPTY_VALUE
    }
    // useGrouping wprost: domyślne pl-PL grupuje dopiero od 5 cyfr, przez co
    // w jednym rzędzie kart sąsiadowałyby "3255,00" i "12 588,00".
    const options = { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true }
    if (CURRENCY_CODE.test(currency || '')) {
        options.style = 'currency'
        options.currency = currency
    }
    return intlFormat(value, locale, options)
}

export function formatCount(value, locale) {
    if (!isNumber(value)) {
        return EMPTY_VALUE
    }
    return intlFormat(value, locale, { maximumFractionDigits: 0 })
}

// paymentSuccessRate to ułamek 0..1, nie liczba procentów.
export function formatRate(rate, locale) {
    if (!isNumber(rate)) {
        return EMPTY_VALUE
    }
    return intlFormat(rate, locale, {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    })
}

export function formatDate(value, format) {
    if (value === null || value === undefined || value === '') {
        return EMPTY_VALUE
    }
    const date = moment(value)
    return date.isValid() ? date.format(format || DATE_TIME_FORMAT) : EMPTY_VALUE
}

// Zmiana względem okresu odniesienia jako ułamek (0.25 = +25%). Brak którejś
// z liczb albo zero w mianowniku daje null — dzielenie nie ma wtedy sensu
// i karta pokaże „—" zamiast Infinity.
export function deltaPercent(current, previous) {
    if (!isNumber(current) || !isNumber(previous) || previous === 0) {
        return null
    }
    return (current - previous) / previous
}

export function formatDelta(delta, locale) {
    if (!isNumber(delta)) {
        return EMPTY_VALUE
    }
    // Znak stawiamy sami: signDisplay bywa ignorowane przez starsze silniki Intl,
    // a bez plusa wzrost i spadek wyglądałyby tak samo.
    const formatted = intlFormat(Math.abs(delta), locale, {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    })
    if (delta > 0) {
        return '+' + formatted
    }
    if (delta < 0) {
        return '-' + formatted
    }
    return formatted
}

export function deltaDirection(delta) {
    if (!isNumber(delta) || delta === 0) {
        return 'flat'
    }
    return delta > 0 ? 'up' : 'down'
}

// Strzałka, kolor i tekst delty liczone razem — widok dostaje gotowy obiekt
// i nie robi już żadnej arytmetyki w szablonie.
export function buildDelta(current, previous, locale, caption) {
    const value = deltaPercent(current, previous)
    const direction = deltaDirection(value)
    return {
        value: value,
        direction: direction,
        text: formatDelta(value, locale),
        iconClass: DELTA_ICONS[direction],
        textClass: DELTA_TEXT_CLASSES[direction],
        caption: caption || ''
    }
}

export function emptyPeriod() {
    return { orders: null, gross: null, paidOrders: null, paidGross: null }
}

// Brak sekcji w odpowiedzi (błąd żądania, starsze API) ma dać pustą kartę,
// a nie wyjątek przy odczycie pola.
export function periodOf(stats, name) {
    const periods = (stats && stats.periods) || {}
    return periods[name] || emptyPeriod()
}

export function last30daysOf(stats) {
    return (stats && stats.last30days) || {}
}

export function currencyOf(stats) {
    return (stats && stats.currency) || null
}

function numberOrZero(value) {
    return isNumber(value) ? value : 0
}

// last30days.daily → dane wykresu. Kolejność zostaje taka, jaką dało API
// (rosnąco, ostatni wpis = dziś); osobno trzymamy pełne daty do tooltipa, bo
// oś X pokazuje tylko DD.MM i sama nie wystarczy do jednoznacznego odczytu.
export function buildChartModel(daily, labels) {
    const rows = Array.isArray(daily) ? daily : []
    const names = labels || {}
    return {
        labels: rows.map(row => moment(row.date).format(CHART_LABEL_FORMAT)),
        fullLabels: rows.map(row => moment(row.date).format(CHART_TOOLTIP_DATE_FORMAT)),
        datasets: [
            {
                type: 'bar',
                label: names.orders || '',
                yAxisID: ORDERS_AXIS_ID,
                data: rows.map(row => numberOrZero(row.orders)),
                backgroundColor: ORDERS_COLOR,
                borderColor: ORDERS_COLOR,
                borderWidth: 0,
                // Słupki pod linią — inaczej linia chowa się za nimi.
                order: 2
            },
            {
                type: 'line',
                label: names.gross || '',
                yAxisID: GROSS_AXIS_ID,
                data: rows.map(row => numberOrZero(row.gross)),
                borderColor: GROSS_COLOR,
                backgroundColor: GROSS_COLOR,
                pointBackgroundColor: GROSS_COLOR,
                fill: false,
                lineTension: 0.3,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 4,
                order: 1
            }
        ]
    }
}

// chart.js dostaje tylko to, co rozumie — fullLabels zostaje w modelu i trafia
// do tooltipa przez domknięcie w buildChartOptions.
export function toChartData(model) {
    return {
        labels: (model && model.labels) || [],
        datasets: (model && model.datasets) || []
    }
}

export function buildChartOptions(model, settings) {
    const options = settings || {}
    const fullLabels = (model && model.fullLabels) || []
    const identity = value => String(value)
    const formatGross = options.formatGross || identity
    const formatOrders = options.formatOrders || identity
    return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: 'top' },
        // mode 'index' — najechanie na dowolny punkt dnia pokazuje obie serie naraz.
        tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
                title: items => {
                    const item = items && items[0]
                    if (!item) {
                        return ''
                    }
                    return fullLabels[item.index] || item.xLabel || ''
                },
                label: (item, data) => {
                    const dataset = data.datasets[item.datasetIndex]
                    const value = dataset.yAxisID === GROSS_AXIS_ID
                        ? formatGross(item.yLabel)
                        : formatOrders(item.yLabel)
                    return dataset.label + ': ' + value
                }
            }
        },
        hover: { mode: 'index', intersect: false },
        scales: {
            xAxes: [{ gridLines: { display: false } }],
            yAxes: [
                {
                    id: ORDERS_AXIS_ID,
                    position: 'left',
                    // Zamówień nie da się mieć 2,5 — bez precision oś dorabiałaby ułamki.
                    ticks: { beginAtZero: true, precision: 0 },
                    scaleLabel: { display: true, labelString: options.ordersAxisLabel || '' }
                },
                {
                    id: GROSS_AXIS_ID,
                    position: 'right',
                    gridLines: { display: false },
                    ticks: { beginAtZero: true, callback: value => formatGross(value) },
                    scaleLabel: { display: true, labelString: options.grossAxisLabel || '' }
                }
            ]
        }
    }
}

// Kompletny model widoku: cztery karty okresów, cztery karty podsumowania
// 30 dni i wykres. Wszystko już sformatowane i przetłumaczone, więc szablon
// tylko wypisuje pola.
export function buildDashboardModel(stats, context) {
    const settings = context || {}
    const locale = settings.locale
    const translate = settings.translate || (key => key)
    const currency = currencyOf(stats)
    const summary = last30daysOf(stats)

    const money = value => formatMoney(value, currency, locale)
    const count = value => formatCount(value, locale)

    const periodCard = (key, titleKey, period, delta) => ({
        key: key,
        title: translate(titleKey),
        orders: count(period.orders),
        gross: money(period.gross),
        paidCaption: translate('dashboard.paidOnline', {
            orders: count(period.paidOrders),
            gross: money(period.paidGross)
        }),
        delta: delta || null
    })

    const today = periodOf(stats, 'today')
    const yesterday = periodOf(stats, 'yesterday')
    const yesterdaySameTime = periodOf(stats, 'yesterdaySameTime')
    const thisWeek = periodOf(stats, 'thisWeek')
    const thisMonth = periodOf(stats, 'thisMonth')
    const prevMonthToDate = periodOf(stats, 'prevMonthToDate')

    const biggestOrder = summary.biggestOrder || null

    const chart = buildChartModel(summary.daily, {
        orders: translate('dashboard.ordersCount'),
        gross: translate('dashboard.gross')
    })

    return {
        currency: currency,
        periodCards: [
            periodCard('today', 'dashboard.today', today,
                buildDelta(today.orders, yesterdaySameTime.orders, locale, translate('dashboard.vsYesterdaySameTime'))),
            periodCard('yesterday', 'dashboard.yesterday', yesterday),
            periodCard('thisWeek', 'dashboard.thisWeek', thisWeek),
            periodCard('thisMonth', 'dashboard.thisMonth', thisMonth,
                buildDelta(thisMonth.orders, prevMonthToDate.orders, locale, translate('dashboard.vsPrevMonth')))
        ],
        summaryCards: [
            {
                key: 'biggestOrder',
                title: translate('dashboard.biggestOrder'),
                value: money(biggestOrder && biggestOrder.gross),
                caption: biggestOrder ? formatDate(biggestOrder.createdDate) : EMPTY_VALUE,
                // Karta prowadzi do szczegółów zamówienia; bez id nie ma dokąd kliknąć.
                link: biggestOrder && biggestOrder.id != null
                    ? '/order-request/' + biggestOrder.id
                    : null
            },
            {
                key: 'avgOrderGross',
                title: translate('dashboard.avgOrder'),
                value: money(summary.avgOrderGross),
                caption: '',
                link: null
            },
            {
                key: 'paymentSuccessRate',
                title: translate('dashboard.paymentSuccess'),
                value: formatRate(summary.paymentSuccessRate, locale),
                caption: '',
                link: null
            },
            {
                key: 'requestsVsPaid',
                title: translate('dashboard.requestsVsPaid'),
                value: count(summary.requestOrders) + ' / ' + count(summary.paidOrders),
                caption: translate('dashboard.requestsCaption'),
                link: null
            }
        ],
        chart: {
            title: translate('dashboard.chartTitle'),
            description: translate('dashboard.chartDescription'),
            empty: chart.labels.length === 0,
            data: toChartData(chart),
            options: buildChartOptions(chart, {
                formatGross: money,
                formatOrders: count,
                ordersAxisLabel: translate('dashboard.ordersCount'),
                grossAxisLabel: translate('dashboard.gross')
            })
        }
    }
}

export default {
    data() {
        return {
            // null do czasu odpowiedzi i po błędzie — model wyliczy wtedy same „—".
            stats: null,
            loading: true
        }
    },
    mounted() {
        this.loadStats()
    },
    methods: {
        // Klikalna jest tylko karta największego zamówienia — reszta ma link
        // równy null i kliknięcie ma wtedy nic nie robić.
        openCard(card) {
            if (card && card.link) {
                this.$router.push(card.link)
            }
        },
        loadStats() {
            this.loading = true
            return axios.get(Const.API_HOST + '/admin/stats/orders')
                .then(response => {
                    this.stats = response.data
                })
                .catch(error => {
                    // Toast o błędzie pokazuje już interceptor z http.js — tutaj
                    // zostawiamy karty w stanie pustym, żeby pulpit się nie wywrócił.
                    this.stats = null
                    // eslint-disable-next-line no-console
                    console.error(error)
                })
                .finally(() => {
                    this.loading = false
                })
        }
    }
}
